import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Desktop.tsx -- the canvas: positioned icons + native-file drop target +
// click-to-clear-selection background. The rest of the desktop (trash, plus,
// chat bubble, drag overlay, preview pane) is mounted by ProjectDesktop
// alongside this component.
//
// Per design \u00a76.4: each catalog entry renders at attrs.position {x, y}.
// New uploads (no position) auto-place on a 100x100 grid skipping occupied
// cells. Drag-to-reposition persists via reposition() from useFolder.
import { useCallback, useMemo, useRef } from 'react';
import { DesktopIcon } from './DesktopIcon.js';
import { useDesktop } from '../../hooks/useDesktop.js';
const GRID_SIZE = 110;
const PADDING = 24;
export function Desktop({ tree, loading, onEnterFolder, onPreviewFile, onUploadFiles, onUploadFilesToFolder, onMoveEntryToFolder, onReposition, onContextMenuEntry, onContextMenuCanvas, }) {
    const { clear, drag, setDragOverTarget } = useDesktop();
    const canvasRef = useRef(null);
    //
    // CRITICAL: do NOT add per-drag-tick state here. The previous
    // implementation kept a `dragGhost` state updated on every `drag`
    // event tick (~60 Hz). React re-renders mutated the DOM tree mid-
    // drag, and Chromium aborts an HTML5 drag operation the moment
    // the DOM tree mutates underneath it. Symptom: dragstart fires
    // ONCE, then no further drag/dragenter/dragover/drop events --
    // looks like the drag silently dies. The user sees the source
    // icon "stuck" at its origin and the drop target never lights up.
    //
    // We rely on the browser's native drag-image (the cursor itself
    // shows the icon's screenshot). When the user wants suppression
    // of the default screenshot, DesktopIcon's onDragStart calls
    // setDragImage(emptyImage) which removes it -- the cursor still
    // tracks but no visual element is rendered, so still no DOM
    // mutation per tick. Result: drag events fire end-to-end.
    // Auto-position entries that don't have a saved position yet.
    const positions = useMemo(() => {
        const out = new Map();
        if (!tree)
            return out;
        const occupied = new Set();
        // First pass: take saved positions.
        for (const e of tree.entries) {
            const p = e.attrs?.position;
            if (p) {
                out.set(e.itemId, p);
                occupied.add(cellKey(p.x, p.y));
            }
        }
        // Second pass: auto-place the rest.
        let cursor = 0;
        const cellsPerRow = 8;
        for (const e of tree.entries) {
            if (out.has(e.itemId))
                continue;
            // Find next free cell.
            while (true) {
                const col = cursor % cellsPerRow;
                const row = Math.floor(cursor / cellsPerRow);
                const x = PADDING + col * GRID_SIZE;
                const y = PADDING + row * GRID_SIZE;
                const k = cellKey(x, y);
                if (!occupied.has(k)) {
                    out.set(e.itemId, { x, y });
                    occupied.add(k);
                    cursor += 1;
                    break;
                }
                cursor += 1;
            }
        }
        return out;
    }, [tree]);
    // Click on background -> clear selection.
    const onCanvasMouseDown = useCallback((e) => {
        if (e.target === canvasRef.current)
            clear();
    }, [clear]);
    const onCanvasContextMenu = useCallback((e) => {
        if (e.target !== canvasRef.current)
            return;
        e.preventDefault();
        onContextMenuCanvas(e.clientX, e.clientY);
    }, [onContextMenuCanvas]);
    // Drop handler covers BOTH native file drops on the canvas AND in-app icon
    // moves to canvas (= reposition). The dataTransfer types tell us which.
    const onCanvasDragOver = useCallback((e) => {
        if (!e.dataTransfer)
            return;
        if (Array.from(e.dataTransfer.types).includes('Files') ||
            e.dataTransfer.types.includes('application/x-fdn-entry')) {
            e.preventDefault();
            setDragOverTarget(null); // canvas-level (no specific folder)
        }
    }, [setDragOverTarget]);
    const onCanvasDrop = useCallback(async (e) => {
        e.preventDefault();
        setDragOverTarget(null);
        // Native files first.
        const files = Array.from(e.dataTransfer.files ?? []);
        if (files.length > 0) {
            const folderTarget = e.target?.closest?.('[data-drop-folder-id]');
            if (folderTarget) {
                const id = folderTarget.getAttribute('data-drop-folder-id');
                await onUploadFilesToFolder(id, files);
            }
            else {
                await onUploadFiles(files);
            }
            return;
        }
        // In-app icon move: target is either a folder icon or the canvas itself.
        const raw = e.dataTransfer.getData('application/x-fdn-entry');
        if (raw) {
            try {
                const entry = JSON.parse(raw);
                const folderTargetEl = e.target?.closest?.('[data-drop-folder-id]');
                if (folderTargetEl && folderTargetEl.getAttribute('data-drop-folder-id') !== entry.itemId) {
                    await onMoveEntryToFolder(entry.itemId, folderTargetEl.getAttribute('data-drop-folder-id'));
                }
                else {
                    // Canvas drop = reposition. We need cursor coords relative to the
                    // canvas, accounting for any scrolling.
                    const rect = canvasRef.current?.getBoundingClientRect();
                    if (rect) {
                        const x = e.clientX - rect.left - 32; // center icon under cursor
                        const y = e.clientY - rect.top - 32;
                        await onReposition(entry.itemId, Math.max(0, x), Math.max(0, y));
                    }
                }
            }
            catch {
                // bad payload
            }
        }
    }, [onUploadFiles, onUploadFilesToFolder, onMoveEntryToFolder, onReposition, setDragOverTarget]);
    const onIconDoubleClick = useCallback((entry) => {
        if (entry.type === 'catalog')
            onEnterFolder(entry.itemId);
        else
            onPreviewFile(entry);
    }, [onEnterFolder, onPreviewFile]);
    return (_jsxs("div", { ref: canvasRef, className: "desktop-canvas", onMouseDown: onCanvasMouseDown, onContextMenu: onCanvasContextMenu, onDragOver: onCanvasDragOver, onDrop: onCanvasDrop, children: [loading && _jsx("div", { className: "desktop-loading", children: 'Loading\u2026' }), !loading && tree && tree.entries.length === 0 && (_jsxs("div", { className: "desktop-empty", children: [_jsx("div", { className: "empty-glyph", children: '\uD83D\uDCC2' }), _jsx("div", { className: "empty-title", children: "This folder is empty" }), _jsxs("div", { className: "empty-hint", children: ["Drag files here, click ", _jsx("strong", { children: "+" }), ", or ask the assistant for help."] })] })), tree?.entries.map((entry) => {
                const pos = positions.get(entry.itemId) ?? { x: PADDING, y: PADDING };
                return (_jsx(DesktopIcon, { entry: entry, position: pos, onDoubleClick: onIconDoubleClick, onContextMenu: onContextMenuEntry }, entry.itemId));
            })] }));
}
function cellKey(x, y) {
    return `${Math.round(x / GRID_SIZE)}:${Math.round(y / GRID_SIZE)}`;
}
